import { useEffect, useRef, useState } from "react";
import { PencilSimple } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/shadcn";
// import { FigmaTypography } from "@libs/zapi-ui/custom";
import {
  PROJECT_ICON_COLORS,
  ProjectDefaultLogo,
  ProjectShapeIcon,
  defaultProjectIconColor,
  getProjectIconPalette,
  isProjectIconColor,
  isProjectShape,
  projectIconColorKeys,
  projectShapeKeys,
  type TProjectIconColor,
  type TProjectShape
} from "@libs/zapi-ui/const";
import { cn } from "@/zapi-frontend/shared/lib/utils";

type TProjectIconProps = {
  shape?: string | null;
  color?: string | null;
  logoUrl?: string | null;
  className?: string;
};

export function ProjectIcon({ shape, color, logoUrl, className }: TProjectIconProps) {
  if (isProjectShape(shape)) {
    const palette = getProjectIconPalette(color);
    return (
      <div
        className={cn("shrink-0 flex items-center justify-center", className)}
        style={{ backgroundColor: palette.iconBackground, color: palette.icon }}
      >
        <ProjectShapeIcon shape={shape} className="size-[62%]" />
      </div>
    );
  }
  if (logoUrl) {
    return <img src={logoUrl} alt="" className={cn("shrink-0 object-cover", className)} />;
  }
  return (
    <div className={cn("shrink-0 flex items-center justify-center bg-innoveraZinc-100", className)}>
      <ProjectDefaultLogo className="size-[62%] text-innoveraZinc-400" />
    </div>
  );
}

// Icon tab hidden until icon generation ships (INV-2268)
// function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
//   return (
//     <button
//       type="button"
//       onClick={onClick}
//       className={cn(
//         "flex-1 h-6 rounded-md text-[13px] leading-4 font-medium transition-colors",
//         active ? "bg-white text-innoveraZinc-900 shadow-sm" : "text-innoveraZinc-500 hover:text-innoveraZinc-700"
//       )}
//     >
//       {children}
//     </button>
//   );
// }

type TColorPickerState = { shape: TProjectShape; left: number; top: number };

const colorPickerTopGap = 2;

type TProjectIconEditorProps = {
  shape: string | null;
  color: string | null;
  logoUrl?: string | null;
  onChange: (shape: TProjectShape, color: TProjectIconColor) => void;
  readOnly?: boolean;
  className?: string;
};

export function ProjectIconEditor({ shape, color, logoUrl, onChange, readOnly = false, className }: TProjectIconEditorProps) {
  const [open, setOpen] = useState(false);
  const [colorPicker, setColorPicker] = useState<TColorPickerState | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  // const [tab, setTab] = useState<"shape" | "icon">("shape");

  useEffect(() => {
    if (!colorPicker) return;
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      if (!colorPickerRef.current?.contains(event.target as Node)) setColorPicker(null);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, [colorPicker]);

  const selectedShape = isProjectShape(shape) ? shape : null;
  const selectedColor = isProjectIconColor(color) ? color : defaultProjectIconColor;

  const icon = <ProjectIcon shape={shape} color={color} logoUrl={logoUrl} className={cn("size-[60px]", className)} />;

  if (readOnly) return icon;

  const handleOpenChange = (nextOpen: boolean) => {
    setColorPicker(null);
    setOpen(nextOpen);
  };

  const handleShapeClick = (event: React.MouseEvent<HTMLButtonElement>, nextShape: TProjectShape) => {
    onChange(nextShape, selectedColor);
    const content = contentRef.current;
    if (!content) return;
    const contentBounds = content.getBoundingClientRect();
    const shapeBounds = event.currentTarget.getBoundingClientRect();
    setColorPicker({
      shape: nextShape,
      left: shapeBounds.left - contentBounds.left - content.clientLeft,
      top: shapeBounds.bottom - contentBounds.top - content.clientTop + colorPickerTopGap
    });
  };

  const handleColorClick = (nextColor: TProjectIconColor) => {
    if (!colorPicker) return;
    onChange(colorPicker.shape, nextColor);
    setColorPicker(null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Edit project icon" className="relative group block w-fit shrink-0 outline-none">
          {icon}
          <span
            className={cn(
              "absolute bottom-[3px] right-[3px] flex items-center justify-center size-6 rounded-md border border-innoveraZinc-150 bg-white text-innoveraZinc-900 shadow-sm transition-opacity",
              open ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            )}
          >
            <PencilSimple className="size-3.5" weight="regular" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent ref={contentRef} align="start" sideOffset={8} className="relative w-[208px] p-1">
        {/* Icon tab hidden until icon generation ships (INV-2268):
        <div className="flex gap-1 p-1 rounded-lg bg-innoveraZinc-100">
          <TabButton active={tab === "shape"} onClick={() => setTab("shape")}>Shape</TabButton>
          <TabButton active={tab === "icon"} onClick={() => setTab("icon")}>Icon</TabButton>
        </div>
        {tab === "icon" && (
          <div className="p-3">
            <FigmaTypography variant="body-3" color="zinc600">
              Please add some project details to create an icon
            </FigmaTypography>
          </div>
        )}
        */}
        <div className="grid grid-cols-5 gap-1.5 p-2">
          {projectShapeKeys.map((key) => (
            <button
              key={key}
              type="button"
              aria-label={`Shape ${key}`}
              onClick={(event) => handleShapeClick(event, key)}
              className={cn(
                "flex items-center justify-center size-8 rounded-lg border border-innoveraZinc-150 text-innoveraZinc-900 transition-colors hover:bg-innoveraZinc-100",
                selectedShape === key && "bg-innoveraZinc-100"
              )}
            >
              <ProjectShapeIcon shape={key} className="size-4" />
            </button>
          ))}
        </div>
        {colorPicker && (
          <div
            ref={colorPickerRef}
            className="absolute w-[196px] rounded-md border bg-popover p-1.5 shadow-md"
            style={{ left: colorPicker.left, top: colorPicker.top }}
          >
            <div className="grid grid-cols-5 gap-1.5">
              {projectIconColorKeys.map((key) => {
                const palette = PROJECT_ICON_COLORS[key];
                return (
                  <button
                    key={key}
                    type="button"
                    aria-label={`Color ${key}`}
                    onClick={() => handleColorClick(key)}
                    className={cn(
                      "flex items-center justify-center size-8 rounded-lg transition-shadow",
                      selectedColor === key && "ring-1 ring-innoveraZinc-300"
                    )}
                    style={{ backgroundColor: palette.iconBackground, color: palette.icon }}
                  >
                    <ProjectShapeIcon shape={colorPicker.shape} className="size-4" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
